/* PuzzleGarden Custom Wishlist (pg-wishlist)
   - App Proxy endpoint: /apps/pg-wishlist
   - Product card endpoint: /products/<handle>?view=wishlist-card
*/

(function () {
  const API_BASE = "/apps/pg-wishlist";
  const LS_KEY = "pgWishlist";
  const MERGE_FLAG = "pgWishlistMerged";
  const VISITOR_KEY = "pgWishlistVisitorId";
  const countRequests = new Map();

  // Session cache for rendered product cards (fast back/forward)
  const CARD_CACHE_PREFIX = "pgWishlistCardHtml:v3:";
  const CARD_CACHE_TTL_MS = 10 * 60 * 1000; // 10 minutes
  const WISHLIST_BATCH_SIZE = 40;
  let wishlistRenderVersion = 0;

  function safeJsonParse(str, fallback) {
    try {
      return JSON.parse(str);
    } catch {
      return fallback;
    }
  }

  function emitWishlistUpdated(detail) {
    window.dispatchEvent(
      new CustomEvent("pg:wishlist:updated", { detail: detail || {} })
    );
  }

  function now() {
    return Date.now();
  }

  function getVisitorId() {
    let id = localStorage.getItem(VISITOR_KEY);
    if (!id) {
      id = window.crypto && crypto.randomUUID
        ? crypto.randomUUID()
        : "v_" + Math.random().toString(16).slice(2) + Date.now();
      localStorage.setItem(VISITOR_KEY, id);
    }
    return id;
  }

  function getGuestItems() {
    const raw = localStorage.getItem(LS_KEY);
    const data = raw ? safeJsonParse(raw, []) : [];
    if (!Array.isArray(data)) return [];
    return data
      .filter((x) => x && (x.productId || x.product_id))
      .map((x) => ({
        productId: String(x.productId || x.product_id),
        productHandle: x.productHandle || x.product_handle || null,
        variantId: x.variantId || x.variant_id || null,
        addedAt: normalizeTimestamp(x.addedAt || x.added_at || x.createdAt || x.created_at),
      }));
  }

  function setGuestItems(items) {
    localStorage.setItem(LS_KEY, JSON.stringify(items));
    sessionStorage.removeItem(MERGE_FLAG);
    emitWishlistUpdated({ source: "guest" });
  }

  function guestHas(productId) {
    return getGuestItems().some((x) => x.productId === String(productId));
  }

  function guestToggle(item) {
    const pid = String(item.productId);
    const items = getGuestItems();
    const idx = items.findIndex((x) => x.productId === pid);

    if (idx >= 0) {
      items.splice(idx, 1);
      setGuestItems(items);
      return { favorited: false };
    }

    items.push({
      productId: pid,
      productHandle: item.productHandle || null,
      variantId: item.variantId || null,
      addedAt: now(),
    });
    setGuestItems(items);
    return { favorited: true };
  }

  function guestRemove(productId) {
    const pid = String(productId);
    const items = getGuestItems();
    const next = items.filter((item) => item.productId !== pid);
    if (next.length === items.length) return { removed: false };
    setGuestItems(next);
    return { removed: true };
  }

  function setGuestFavorite(item, favorited) {
    const pid = String(item.productId);
    const items = getGuestItems();
    const existing = items.findIndex((entry) => entry.productId === pid);
    if (favorited && existing < 0) {
      items.push({
        productId: pid,
        productHandle: item.productHandle || null,
        variantId: item.variantId || null,
        addedAt: now(),
      });
      setGuestItems(items);
    } else if (!favorited && existing >= 0) {
      items.splice(existing, 1);
      setGuestItems(items);
    }
    return { favorited: !!favorited };
  }

  async function apiGet(params) {
    const p = { ...params, visitor_id: getVisitorId() };
    const url = API_BASE + "?" + new URLSearchParams(p).toString();
    const res = await fetch(url, {
      credentials: "same-origin",
      cache: "no-store",
      headers: { Accept: "application/json" },
    });
    const text = await res.text();
    let json = null;
    try {
      json = JSON.parse(text);
    } catch {}
    return { ok: res.ok, status: res.status, json, text };
  }

  async function apiPostMerge(productIds) {
    const url =
      API_BASE +
      "?" +
      new URLSearchParams({ action: "merge", visitor_id: getVisitorId() }).toString();

    const res = await fetch(url, {
      method: "POST",
      credentials: "same-origin",
      cache: "no-store",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({ productIds }),
    });

    const text = await res.text();
    let json = null;
    try {
      json = JSON.parse(text);
    } catch {}
    return { ok: res.ok, status: res.status, json, text };
  }

  async function apiPostJson(action, body) {
    const url =
      API_BASE +
      "?" +
      new URLSearchParams({ action, visitor_id: getVisitorId() }).toString();

    const res = await fetch(url, {
      method: "POST",
      credentials: "same-origin",
      cache: "no-store",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify(body || {}),
    });

    const text = await res.text();
    let json = null;
    try {
      json = JSON.parse(text);
    } catch {}
    return { ok: res.ok, status: res.status, json, text };
  }

  function isLoginRequired(res) {
    return res.status === 401 || (res.json && res.json.error === "login_required");
  }

  async function fetchList() {
    const res = await apiGet({ action: "list" });

    if (res.ok && res.json && res.json.ok) {
      if (res.json.mode === "guest") {
        const serverItems = normalizeWishlistItems(res.json.items);
        const combined = new Map();
        [...serverItems, ...getGuestItems()].forEach((item) => {
          combined.set(item.productId, item);
        });
        return { mode: "guest", items: [...combined.values()] };
      }
      const items = Array.isArray(res.json.items)
        ? res.json.items
        : Array.isArray(res.json.productIds)
          ? res.json.productIds.map((id) => ({ productId: String(id) }))
          : [];

      return { mode: res.json.mode || "logged-in", items };
    }

    // fallback to guest
    return { mode: "guest", items: getGuestItems(), error: res.json || res.text };
  }

  function normalizeWishlistItems(items) {
    return (Array.isArray(items) ? items : [])
      .map((it) => ({
        productId: String(it.productId || it.product_id || ""),
        productHandle: it.productHandle || it.product_handle || null,
        variantId: it.variantId || it.variant_id || null,
        addedAt: normalizeTimestamp(it.addedAt || it.added_at || it.createdAt || it.created_at),
      }))
      .filter((x) => x.productId);
  }

  function normalizeTimestamp(value) {
    if (!value) return 0;
    if (typeof value === "number") return value;
    if (typeof value === "string") {
      const parsed = Date.parse(value);
      return Number.isFinite(parsed) ? parsed : 0;
    }
    const seconds = value.seconds || value._seconds;
    return Number.isFinite(Number(seconds)) ? Number(seconds) * 1000 : 0;
  }

  function setHeaderCount(count) {
    const bubble = document.getElementById("pg-wishlist-header-bubble");
    const counter = document.getElementById("pg-wishlist-header-count");
    if (!bubble || !counter) return;

    const n = Math.max(0, Number(count) || 0);
    counter.textContent = String(n);
    bubble.style.display = n > 0 ? "inline-flex" : "none";
  }

  function setProductButtonState(btn, favorited) {
    if (!btn) return;

    const isFav = !!favorited;
    const addLabel = btn.dataset.labelAdd || "Hozzáadás a kedvencekhez";
    const removeLabel = btn.dataset.labelRemove || "Törlés a kedvencek közül";
    const label = isFav ? removeLabel : addLabel;

    btn.dataset.state = isFav ? "filled" : "outline";
    btn.setAttribute("aria-pressed", isFav ? "true" : "false");
    btn.setAttribute("aria-label", label);
    const labelElement = btn.querySelector(".pg-wishlist-product-btn__label");
    if (labelElement) {
      labelElement.textContent = label;
    } else {
      btn.textContent = label;
    }
  }

  function applyWishlistUiState(items) {
    const normalized = normalizeWishlistItems(items);
    const ids = new Set(normalized.map((x) => x.productId));

    setHeaderCount(normalized.length);

    document.querySelectorAll(".pg-wishlist-heart").forEach((btn) => {
      const pid = String(btn.dataset.productId || "");
      if (!pid) return;
      setHeartState(btn, ids.has(pid) ? "filled" : "outline");
    });

    document.querySelectorAll("[data-pg-wishlist-product-btn]").forEach((btn) => {
      const pid = String(btn.dataset.productId || "");
      if (!pid) return;
      setProductButtonState(btn, ids.has(pid));
    });
  }

  async function refreshWishlistUi() {
    try {
      const list = await fetchList();
      applyWishlistUiState(list.items);
    } catch {
      applyWishlistUiState(getGuestItems());
    }
  }

  async function maybeMergeGuestOnLogin() {
    if (sessionStorage.getItem(MERGE_FLAG) === "1") return;

    const guest = getGuestItems();
    if (!guest.length) return;

    const listRes = await apiGet({ action: "list" });
    if (!(listRes.ok && listRes.json && listRes.json.ok)) return;
    if (listRes.json.mode === "guest") return;

    let completed = true;

    for (const item of guest) {
      const claimRes = await apiGet({
        action: "claim",
        product_id: item.productId,
      });
      if (!(claimRes.ok && claimRes.json && claimRes.json.ok)) {
        completed = false;
        break;
      }
    }

    if (completed) {
      localStorage.removeItem(LS_KEY);
      emitWishlistUpdated({ source: "merge" });
      sessionStorage.setItem(MERGE_FLAG, "1");
    }
  }

  function setHeartState(btn, state) {
    btn.dataset.state = state;
    btn.setAttribute("aria-pressed", state === "filled" ? "true" : "false");
  }

  function setCounter(el, n) {
    const v = Number.isFinite(n) ? n : 0;
    el.textContent = String(v);
    el.style.display = v > 0 ? "inline-block" : "none";
  }

  function fetchProductCount(productId, force) {
    const key = String(productId);
    if (!force && countRequests.has(key)) return countRequests.get(key);
    const request = apiGet({ action: "count", product_id: key }).finally(() => {
      window.setTimeout(() => countRequests.delete(key), 30000);
    });
    countRequests.set(key, request);
    return request;
  }

  function announceStatus(message) {
    const status = document.getElementById("pg-wishlist-status");
    if (status) status.textContent = message || "";
  }

  function updateSavedTabCount(id, count) {
    const badge = document.getElementById(id);
    if (!badge) return;
    const value = Math.max(0, Number(count) || 0);
    badge.textContent = String(value);
    badge.hidden = value < 1;
  }

  function decrementSavedTabCount(id) {
    const badge = document.getElementById(id);
    if (!badge) return;
    updateSavedTabCount(id, Math.max(0, Number(badge.textContent || 0) - 1));
  }

  function initSavedTabs() {
    const page = document.querySelector("[data-pg-wishlist-page]");
    if (!page || page.dataset.pgSavedTabsInit === "1") return;
    const tabs = Array.from(page.querySelectorAll("[data-pg-saved-tab]"));
    if (!tabs.length) return;
    page.dataset.pgSavedTabsInit = "1";

    const activate = (name, focus) => {
      tabs.forEach((tab) => {
        const active = tab.dataset.pgSavedTab === name;
        tab.setAttribute("aria-selected", active ? "true" : "false");
        tab.tabIndex = active ? 0 : -1;
        const panelId = tab.getAttribute("aria-controls");
        const panel = panelId ? document.getElementById(panelId) : null;
        if (panel) panel.hidden = !active;
        if (active && focus) tab.focus();
      });
    };

    tabs.forEach((tab, index) => {
      tab.addEventListener("click", () => activate(tab.dataset.pgSavedTab, false));
      tab.addEventListener("keydown", (event) => {
        let nextIndex = null;
        if (event.key === "ArrowRight") nextIndex = (index + 1) % tabs.length;
        if (event.key === "ArrowLeft") nextIndex = (index - 1 + tabs.length) % tabs.length;
        if (event.key === "Home") nextIndex = 0;
        if (event.key === "End") nextIndex = tabs.length - 1;
        if (nextIndex === null) return;
        event.preventDefault();
        activate(tabs[nextIndex].dataset.pgSavedTab, true);
      });
    });

    activate(tabs.find((tab) => tab.getAttribute("aria-selected") === "true")?.dataset.pgSavedTab || "wishlist", false);
  }

  function backInStockStatusLabel(status) {
    const english = (document.documentElement.lang || "").toLowerCase().startsWith("en");
    const labels = english
      ? {
          pending: "Waiting for restock",
          first_sent: "First alert sent",
          reminder_sent: "Final alert sent",
          first_email_failed: "Alert delivery will be retried",
          reminder_email_failed: "Reminder delivery will be retried",
        }
      : {
          pending: "Készletre vár",
          first_sent: "Első értesítő elküldve",
          reminder_sent: "Utolsó értesítő elküldve",
          first_email_failed: "Az értesítő küldését újrapróbáljuk",
          reminder_email_failed: "Az emlékeztető küldését újrapróbáljuk",
        };
    return labels[status] || (english ? "Active stock alert" : "Aktív termékértesítő");
  }

  function formatBackInStockDate(value) {
    const date = new Date(value || "");
    if (!Number.isFinite(date.getTime())) return "";
    const locale = (document.documentElement.lang || "hu").toLowerCase().startsWith("en") ? "en-GB" : "hu-HU";
    return new Intl.DateTimeFormat(locale, { year: "numeric", month: "long", day: "numeric" }).format(date);
  }

  function createBackInStockAccountItem(item, list, empty) {
    const page = document.querySelector("[data-pg-wishlist-page]");
    const row = document.createElement("li");
    row.className = "pg-bis-account-item";
    row.dataset.subscriptionId = String(item.subscriptionId || "");

    const details = document.createElement("div");
    const title = item.productHandle ? document.createElement("a") : document.createElement("span");
    title.className = "pg-bis-account-title";
    title.textContent = String(item.productTitle || "Termékértesítő");
    if (item.productHandle) title.href = `/products/${encodeURIComponent(String(item.productHandle))}`;
    details.appendChild(title);

    if (item.productVendor) {
      const vendor = document.createElement("p");
      vendor.className = "pg-bis-account-vendor";
      vendor.textContent = String(item.productVendor);
      details.appendChild(vendor);
    }

    if (item.variantTitle && String(item.variantTitle).toLowerCase() !== "default title") {
      const variant = document.createElement("p");
      variant.className = "pg-bis-account-variant";
      variant.textContent = String(item.variantTitle);
      details.appendChild(variant);
    }

    const status = document.createElement("span");
    status.className = "pg-bis-account-status";
    status.textContent = backInStockStatusLabel(String(item.status || "pending"));
    details.appendChild(status);

    const dateValue = formatBackInStockDate(item.createdAt);
    if (dateValue) {
      const date = document.createElement("p");
      date.className = "pg-bis-account-date";
      date.textContent = `${page?.dataset.pgBisDateLabel || "Feliratkozás"}: ${dateValue}`;
      details.appendChild(date);
    }

    const button = document.createElement("button");
    button.type = "button";
    button.className = "button button--secondary pg-bis-account-unsubscribe";
    button.textContent = page?.dataset.pgBisUnsubscribeLabel || "Leiratkozás";
    button.addEventListener("click", async () => {
      const prompt = page?.dataset.pgBisUnsubscribeConfirm || "Biztosan leiratkozol erről a termékértesítőről?";
      if (!window.confirm(prompt)) return;

      button.disabled = true;
      button.setAttribute("aria-busy", "true");
      try {
        const response = await apiPostJson("bis_account_unsubscribe", {
          subscription_id: String(item.subscriptionId || ""),
        });
        if (!response.ok || !response.json?.ok) throw new Error("unsubscribe_failed");
        row.remove();
        decrementSavedTabCount("pg-bis-tab-count");
        if (!list.children.length && empty) empty.hidden = false;
        announceStatus("Sikeresen leiratkoztál a termékértesítőről.");
      } catch {
        button.disabled = false;
        announceStatus("A leiratkozás nem sikerült. Kérjük, próbáld újra.");
      } finally {
        button.removeAttribute("aria-busy");
      }
    });

    row.append(details, button);
    return row;
  }

  async function renderBackInStockAccountPanel() {
    const panel = document.querySelector("[data-pg-bis-account-panel]");
    if (!panel || panel.dataset.pgCustomerLoggedIn !== "true") return;
    const list = document.getElementById("pg-bis-account-list");
    const loading = document.getElementById("pg-bis-account-loading");
    const empty = document.getElementById("pg-bis-account-empty");
    const error = document.getElementById("pg-bis-account-error");
    if (!list || !empty) return;

    if (loading) loading.hidden = false;
    if (error) error.hidden = true;
    empty.hidden = true;
    list.innerHTML = "";

    try {
      const response = await apiGet({ action: "bis_account_list" });
      if (!response.ok || !response.json?.ok) throw new Error(response.json?.error || "load_failed");
      const items = Array.isArray(response.json.items) ? response.json.items : [];
      updateSavedTabCount("pg-bis-tab-count", items.length);
      items.forEach((item) => list.appendChild(createBackInStockAccountItem(item, list, empty)));
      empty.hidden = items.length > 0;
    } catch {
      if (error) {
        const english = (document.documentElement.lang || "").toLowerCase().startsWith("en");
        error.textContent = english
          ? "Stock alerts could not be loaded. Please try again."
          : "A termékértesítőket nem sikerült betölteni. Kérjük, próbáld újra.";
        error.hidden = false;
      }
    } finally {
      if (loading) loading.hidden = true;
    }
  }

  function sortWishlistGrid() {
    const grid = document.getElementById("pg-wishlist-grid");
    const select = document.getElementById("PgWishlistSortBy");
    if (!grid || !select) return;

    const cards = Array.from(grid.querySelectorAll(":scope > li.grid__item"));
    const locale = document.documentElement.lang || "hu";
    const sortValue = select.value;

    cards.sort((a, b) => {
      if (sortValue === "title-ascending" || sortValue === "title-descending") {
        const result = (a.dataset.pgProductTitle || "").localeCompare(
          b.dataset.pgProductTitle || "",
          locale,
          { sensitivity: "base" }
        );
        return sortValue === "title-descending" ? -result : result;
      }

      if (sortValue === "price-ascending" || sortValue === "price-descending") {
        const result = Number(a.dataset.pgProductPrice || 0) - Number(b.dataset.pgProductPrice || 0);
        return sortValue === "price-descending" ? -result : result;
      }

      if (sortValue === "availability") {
        const aAvailable = a.dataset.pgProductAvailable === "true" ? 1 : 0;
        const bAvailable = b.dataset.pgProductAvailable === "true" ? 1 : 0;
        const availabilityResult = bAvailable - aAvailable;
        if (availabilityResult !== 0) return availabilityResult;
      }

      return Number(a.dataset.pgRecentOrder || 0) - Number(b.dataset.pgRecentOrder || 0);
    });

    cards.forEach((card) => grid.appendChild(card));
  }

  function initWishlistSorting() {
    const select = document.getElementById("PgWishlistSortBy");
    if (!select || select.dataset.pgInit === "1") return;
    select.dataset.pgInit = "1";
    select.addEventListener("change", sortWishlistGrid);
  }

  function upgradeRecommendationPlaceholders(root) {
    const scope = root || document;
    const placeholders = [];

    if (scope instanceof HTMLElement && scope.matches(".gw-add-to-wishlist-product-card-placeholder")) {
      placeholders.push(scope);
    }
    scope.querySelectorAll?.(".gw-add-to-wishlist-product-card-placeholder").forEach((el) => {
      placeholders.push(el);
    });

    placeholders.forEach((placeholder) => {
      if (placeholder.dataset.pgWishlistUpgraded === "1") return;

      const productId = placeholder.dataset.gwProductId || "";
      const variantId = placeholder.dataset.gwVariantId || "";
      const card = placeholder.closest(".card-wrapper");
      const productLink = card?.querySelector('a[href*="/products/"]');
      const handleMatch = productLink?.getAttribute("href")?.match(/\/products\/([^/?#]+)/);
      const productHandle = handleMatch ? handleMatch[1] : "";
      if (!productId || !productHandle) return;

      placeholder.dataset.pgWishlistUpgraded = "1";
      placeholder.className = "pg-wishlist-heart-container pg-wishlist-recommendation";
      placeholder.setAttribute("data-pg-wishlist", "");
      placeholder.innerHTML = `
        <button
          type="button"
          class="pg-wishlist-heart"
          data-state="outline"
          aria-label="Kedvencekhez"
          title="Kedvencekhez"
        >
          <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false">
            <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41 0.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"></path>
          </svg>
          <span class="pg-wishlist-counter" data-pg-wishlist-counter aria-hidden="true">0</span>
        </button>`;
      const button = placeholder.querySelector(".pg-wishlist-heart");
      button.dataset.productId = productId;
      button.dataset.productHandle = productHandle;
      button.dataset.variantId = variantId;
      placeholder.dataset.pgInit = "1";
      initHeart(placeholder);
    });
  }

  async function removeGuestEverywhere(productId, productHandle) {
    const pid = String(productId);
    const listRes = await apiGet({ action: "list" });
    const serverItems = listRes.ok && listRes.json && listRes.json.mode === "guest"
      ? normalizeWishlistItems(listRes.json.items)
      : [];
    const existsOnServer = serverItems.some((item) => item.productId === pid);

    if (existsOnServer) {
      const toggleRes = await apiGet({
        action: "toggle",
        product_id: pid,
        product_handle: productHandle || "",
      });
      if (!(toggleRes.ok && toggleRes.json && toggleRes.json.ok && toggleRes.json.favorited === false)) {
        return false;
      }
    }

    const localResult = guestRemove(pid);
    if (existsOnServer && !localResult.removed) {
      emitWishlistUpdated({ source: "guest-remove", productId: pid });
    }
    return existsOnServer || localResult.removed;
  }

  async function initHeart(container) {
    const btn = container.querySelector(".pg-wishlist-heart");
    const counter = container.querySelector(".pg-wishlist-counter");
    if (!btn || !counter) return;

    const productId = btn.dataset.productId;
    const productHandle = btn.dataset.productHandle || "";
    const variantId = btn.dataset.variantId || "";

    setHeartState(btn, guestHas(productId) ? "filled" : "outline");

    const toggleHeart = async () => {
      const prev = btn.dataset.state;
      setHeartState(btn, "loading");

      try {
        const res = await apiGet({
          action: "toggle",
          product_id: productId,
          product_handle: productHandle,
          variant_id: variantId,
        });

        if (isLoginRequired(res)) {
          const out = guestToggle({ productId, productHandle, variantId });
          setHeartState(btn, out.favorited ? "filled" : "outline");
          return;
        }

        if (res.ok && res.json && res.json.ok) {
          countRequests.delete(String(productId));
          if (res.json.mode === "guest") {
            setGuestFavorite({ productId, productHandle, variantId }, !!res.json.favorited);
          }
          setHeartState(btn, res.json.favorited ? "filled" : "outline");
          if (typeof res.json.count !== "undefined") {
            setCounter(counter, Number(res.json.count || 0));
          }
          emitWishlistUpdated({
            source: "heart",
            favorited: !!res.json.favorited,
            productId,
          });
          return;
        }

        setHeartState(btn, prev || "outline");
      } catch {
        setHeartState(btn, prev || "outline");
      }
    };

    btn.pgWishlistToggle = toggleHeart;
    btn.addEventListener("click", async (e) => {
      e.preventDefault();
      e.stopPropagation();
      await toggleHeart();
    });

    try {
      const res = await fetchProductCount(productId);
      if (res.ok && res.json && res.json.ok) {
        setCounter(counter, Number(res.json.count || 0));
        if (typeof res.json.isFavorited === "boolean" && !(guestHas(productId) && !res.json.isFavorited)) {
          setHeartState(btn, res.json.isFavorited ? "filled" : "outline");
        }
      }
    } catch {}
  }

  function initAllHearts(root) {
    (root || document)
      .querySelectorAll(".pg-wishlist-heart-container")
      .forEach((c) => {
        if (c.dataset.pgInit === "1") return;
        c.dataset.pgInit = "1";
        initHeart(c);
      });
  }

  async function initProductButton(btn) {
    if (!btn || btn.dataset.pgInit === "1") return;
    btn.dataset.pgInit = "1";

    const productId = btn.dataset.productId;
    const productHandle = btn.dataset.productHandle || "";
    const variantId = btn.dataset.variantId || "";

    setProductButtonState(btn, guestHas(productId));

    btn.addEventListener("click", async (e) => {
      e.preventDefault();

      const wasDisabled = btn.disabled;
      const prevFavorited = btn.dataset.state === "filled";
      btn.disabled = true;
      btn.setAttribute("aria-busy", "true");

      try {
        const res = await apiGet({
          action: "toggle",
          product_id: productId,
          product_handle: productHandle,
          variant_id: variantId,
        });

        if (isLoginRequired(res)) {
          const out = guestToggle({ productId, productHandle, variantId });
          setProductButtonState(btn, out.favorited);
          return;
        }

        if (res.ok && res.json && res.json.ok) {
          countRequests.delete(String(productId));
          if (res.json.mode === "guest") {
            setGuestFavorite({ productId, productHandle, variantId }, !!res.json.favorited);
          }
          setProductButtonState(btn, !!res.json.favorited);
          emitWishlistUpdated({
            source: "product-button",
            favorited: !!res.json.favorited,
            productId,
          });
          return;
        }

        setProductButtonState(btn, prevFavorited);
      } catch {
        setProductButtonState(btn, prevFavorited);
      } finally {
        btn.disabled = wasDisabled;
        btn.removeAttribute("aria-busy");
      }
    });

    try {
      const res = await fetchProductCount(productId);
      if (
        res.ok &&
        res.json &&
        res.json.ok &&
        typeof res.json.isFavorited === "boolean" &&
        !(guestHas(productId) && !res.json.isFavorited)
      ) {
        setProductButtonState(btn, res.json.isFavorited);
      }
    } catch {}
  }

  function initAllProductButtons(root) {
    (root || document)
      .querySelectorAll("[data-pg-wishlist-product-btn]")
      .forEach((btn) => {
        initProductButton(btn);
      });
  }

  // --- FAST: limited concurrency fetch pool ---
  async function mapLimit(items, limit, mapper) {
    const results = new Array(items.length);
    let i = 0;

    const workers = new Array(Math.min(limit, items.length)).fill(0).map(async () => {
      while (i < items.length) {
        const idx = i++;
        try {
          results[idx] = await mapper(items[idx], idx);
        } catch {
          results[idx] = "";
        }
      }
    });

    await Promise.all(workers);
    return results;
  }

  function cardCacheKey(handle) {
    return CARD_CACHE_PREFIX + handle;
  }

  function getCachedCard(handle) {
    try {
      const raw = sessionStorage.getItem(cardCacheKey(handle));
      if (!raw) return null;
      const obj = safeJsonParse(raw, null);
      if (!obj || !obj.html || !obj.ts) return null;
      if (now() - obj.ts > CARD_CACHE_TTL_MS) return null;
      return obj.html;
    } catch {
      return null;
    }
  }

  function setCachedCard(handle, html) {
    try {
      sessionStorage.setItem(
        cardCacheKey(handle),
        JSON.stringify({ ts: now(), html })
      );
    } catch {}
  }

  function wireWishlistCardActions(root) {
    const scope = root || document;

    scope.querySelectorAll('[data-pg-add-to-cart="1"]').forEach((btn) => {
      if (btn.dataset.pgWishlistActionInit === "1") return;
      btn.dataset.pgWishlistActionInit = "1";
      btn.addEventListener("click", async (e) => {
        e.preventDefault();
        const vid = btn.getAttribute("data-variant-id");
        if (!vid) return;

        btn.setAttribute("aria-busy", "true");
        try {
          await fetch("/cart/add.js", {
            method: "POST",
            credentials: "same-origin",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ items: [{ id: Number(vid), quantity: 1 }] }),
          });
        } finally {
          btn.removeAttribute("aria-busy");
        }
      });
    });

    scope.querySelectorAll('[data-pg-remove="1"]').forEach((btn) => {
      if (btn.dataset.pgWishlistActionInit === "1") return;
      btn.dataset.pgWishlistActionInit = "1";
      btn.addEventListener("click", async (e) => {
        e.preventDefault();

        const pid = btn.getAttribute("data-product-id");
        const handle = btn.getAttribute("data-product-handle");
        if (!pid) return;

        btn.setAttribute("aria-busy", "true");
        try {
          const list = await fetchList();
          let removed = false;

          if (list.mode === "guest") {
            removed = await removeGuestEverywhere(pid, handle);
          } else {
            const res = await apiGet({
              action: "toggle",
              product_id: pid,
              product_handle: handle || "",
            });
            removed = !!(res.ok && res.json && res.json.ok && res.json.favorited === false);
            if (removed) {
              countRequests.delete(String(pid));
              emitWishlistUpdated({ source: "wishlist-page-remove", productId: pid });
            }
          }

          if (!removed) {
            announceStatus("A terméket nem sikerült eltávolítani. Kérjük, próbáld újra.");
            return;
          }

          btn.closest("li.grid__item")?.remove();
          decrementSavedTabCount("pg-wishlist-tab-count");
          announceStatus("A terméket eltávolítottuk a kedvencek közül.");
        } catch {
          announceStatus("A terméket nem sikerült eltávolítani. Kérjük, próbáld újra.");
        } finally {
          btn.removeAttribute("aria-busy");
        }
      });
    });
  }

  async function renderWishlistPage() {
    const page = document.querySelector("[data-pg-wishlist-page]");
    if (!page) return;

    const renderVersion = ++wishlistRenderVersion;

    const grid = document.getElementById("pg-wishlist-grid");
    const empty = document.getElementById("pg-wishlist-empty");
    const loading = document.getElementById("pg-wishlist-loading");
    const sortForm = document.getElementById("pg-wishlist-sort-form");
    const loadMoreWrap = document.getElementById("pg-wishlist-load-more-wrap");
    const loadMoreButton = document.getElementById("pg-wishlist-load-more");
    const loadMoreProgress = document.getElementById("pg-wishlist-load-more-progress");
    if (!grid || !empty) return;

    if (loading) loading.style.display = "block";
    if (loadMoreWrap) loadMoreWrap.style.display = "none";

    const list = await fetchList();
    if (renderVersion !== wishlistRenderVersion) return;
    const items = Array.isArray(list.items) ? list.items : [];

    if (!items.length) {
      updateSavedTabCount("pg-wishlist-tab-count", 0);
      empty.style.display = "block";
      if (sortForm) sortForm.style.display = "none";
      grid.innerHTML = "";
      if (loading) loading.style.display = "none";
      if (loadMoreWrap) loadMoreWrap.style.display = "none";
      return;
    }

    empty.style.display = "none";
    if (sortForm) sortForm.style.display = "flex";
    grid.innerHTML = "";

    const normalized = normalizeWishlistItems(items).filter((x) => x.productHandle);
    updateSavedTabCount("pg-wishlist-tab-count", normalized.length);
    normalized.sort((a, b) => {
      if (a.addedAt && b.addedAt) return b.addedAt - a.addedAt;
      if (a.addedAt) return -1;
      if (b.addedAt) return 1;
      return 0;
    });

    if (!normalized.length) {
      empty.style.display = "block";
      if (sortForm) sortForm.style.display = "none";
      if (loading) loading.style.display = "none";
      if (loadMoreWrap) loadMoreWrap.style.display = "none";
      return;
    }

    let cursor = 0;
    let loadingBatch = false;
    const loadMoreLabel = page.dataset.pgLoadMoreLabel || "További kedvencek betöltése";
    const progressLabel = page.dataset.pgProgressLabel || "termék betöltve";

    const updateLoadMoreControls = () => {
      const hasMore = cursor < normalized.length;
      if (loadMoreWrap) loadMoreWrap.style.display = hasMore ? "flex" : "none";
      if (loadMoreButton) {
        loadMoreButton.disabled = loadingBatch;
        loadMoreButton.textContent = `${loadMoreLabel} (${cursor} / ${normalized.length})`;
      }
      if (loadMoreProgress) {
        loadMoreProgress.textContent = `${cursor} / ${normalized.length} ${progressLabel}`;
      }
    };

    const appendNextBatch = async () => {
      if (loadingBatch || cursor >= normalized.length) return;
      loadingBatch = true;
      const startIndex = cursor;
      const batchItems = normalized.slice(startIndex, startIndex + WISHLIST_BATCH_SIZE);
      cursor += batchItems.length;
      if (loading) loading.style.display = "block";
      updateLoadMoreControls();

      try {
        const htmlBlocks = await mapLimit(batchItems, 8, async (it) => {
          const handle = it.productHandle;
          const cached = getCachedCard(handle);
          if (cached) return cached;

          const url = `/products/${handle}?view=wishlist-card`;
          const res = await fetch(url, { credentials: "same-origin" });
          if (!res.ok) return "";
          const html = await res.text();
          if (html && html.indexOf("<li") !== -1) setCachedCard(handle, html);
          return html;
        });

        if (renderVersion !== wishlistRenderVersion) return;

        htmlBlocks.forEach((html, offset) => {
          if (!html) return;
          const fragment = document.createRange().createContextualFragment(html);
          const card = fragment.querySelector("li.grid__item");
          if (card) card.dataset.pgRecentOrder = String(startIndex + offset);
          grid.appendChild(fragment);
        });

        sortWishlistGrid();
        initAllHearts(grid);
        initAllProductButtons(grid);
        wireWishlistCardActions(grid);
        announceStatus(`${cursor} kedvenc betöltve a(z) ${normalized.length} termékből.`);
      } finally {
        loadingBatch = false;
        if (renderVersion === wishlistRenderVersion) {
          if (loading) loading.style.display = "none";
          updateLoadMoreControls();
        }
      }
    };

    if (loadMoreButton) loadMoreButton.onclick = appendNextBatch;
    await appendNextBatch();
  }

  function observeDom() {
    const obs = new MutationObserver((muts) => {
      for (const m of muts) {
        m.addedNodes.forEach((n) => {
          if (!(n instanceof HTMLElement)) return;
          upgradeRecommendationPlaceholders(n);
          initAllHearts(n);
          initAllProductButtons(n);
        });
      }
    });
    obs.observe(document.body, { childList: true, subtree: true });
  }

  function interceptRecommendationClicks() {
    const recommendationButton = (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return null;
      return target.closest(".pg-wishlist-recommendation .pg-wishlist-heart");
    };

    ["pointerdown", "mousedown", "pointerup", "mouseup"].forEach((type) => {
      window.addEventListener(type, (event) => {
        if (!recommendationButton(event)) return;
        event.stopImmediatePropagation();
      }, true);
    });

    window.addEventListener("click", (event) => {
      const button = recommendationButton(event);
      if (!button) return;

      event.preventDefault();
      event.stopImmediatePropagation();
      button.pgWishlistToggle?.();
    }, true);
  }

  async function boot() {
    upgradeRecommendationPlaceholders(document);
    initAllHearts(document);
    initAllProductButtons(document);
    initWishlistSorting();
    initSavedTabs();
    interceptRecommendationClicks();
    observeDom();

    window.addEventListener("pg:wishlist:updated", () => {
      refreshWishlistUi();
      renderWishlistPage();
    });

    window.addEventListener("storage", (e) => {
      if (e.key !== LS_KEY) return;
      refreshWishlistUi();
      renderWishlistPage();
    });

    await maybeMergeGuestOnLogin();
    await refreshWishlistUi();
    await Promise.all([renderWishlistPage(), renderBackInStockAccountPanel()]);
    upgradeRecommendationPlaceholders(document);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", boot);
  } else {
    boot();
  }
})();
